import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'

/**
 * One circular open at a time.
 *
 * An expanded card is tall - facts, tabs, source documents and a conversation -
 * so two of them open at once means the second is read by scrolling past the
 * first, and the feed stops being a list. Both the dashboard's Recent Circulars
 * and the Circular Feed opened as many as you clicked, because each row kept
 * its own `open` flag and nothing knew about the others.
 *
 * The rule lives here rather than in either list, so the two cannot drift, and
 * a card used outside a provider still works on its own state - nothing has to
 * be wrapped to keep functioning.
 */

interface Expanded {
  openId: string | null
  setOpenId: (id: string | null) => void
}

const ExpandedContext = createContext<Expanded | null>(null)

export function OneCircularOpen({ children }: { children: ReactNode }) {
  const [openId, setOpenId] = useState<string | null>(null)
  return (
    <ExpandedContext.Provider value={{ openId, setOpenId }}>{children}</ExpandedContext.Provider>
  )
}

/**
 * Whether this circular is the open one, and how to toggle it.
 *
 * `initiallyOpen` is for a card arrived at from a link - the feed's
 * `?focus=<code>` - which should open itself and, being the one the person came
 * for, close whatever else was open.
 */
export function useExpanded(id: string, initiallyOpen = false): [boolean, () => void] {
  const shared = useContext(ExpandedContext)
  const [local, setLocal] = useState(initiallyOpen)

  useEffect(() => {
    if (initiallyOpen && shared) shared.setOpenId(id)
    // Only on the way in: re-running this would fight a person's own clicks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initiallyOpen, id])

  if (shared) {
    return [shared.openId === id, () => shared.setOpenId(shared.openId === id ? null : id)]
  }
  return [local, () => setLocal((v) => !v)]
}
